import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { BsFillQuestionDiamondFill as InfoIcon } from 'react-icons/bs';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { useSpring, animated } from 'react-spring';

import LinearProgress from '../../../components/LinearProgress';
import { parseNumberToString } from '../../../utils/parseData';
import {
  getApplianceLabel,
  sortAppliancesDescending,
} from '../../../utils/appliances';

const parseDataObjectToArray = (dataWithoutNumberOfDevices) => {
  const keys = Object.keys(dataWithoutNumberOfDevices);
  const array = keys.map((key) => {
    return {
      key: key,
      title: getApplianceLabel(key.replace('Percentage', '')),
      percentage: dataWithoutNumberOfDevices[key].percentage,
      numberOfDevices: dataWithoutNumberOfDevices[key].numberOfDevices,
    };
  });
  const sortedArray = sortAppliancesDescending(array);
  return sortedArray;
};

const rowHasTooltip = (row) => {
  return row.key === "withoutAppliancesPercentage" 
      || row.key === "withoutPropertyDetailsPercentage"
      ? true : false;
}
function ReportedTable({ data }) {
  const dataWithoutNumberOfDevices = { ...data };
  delete dataWithoutNumberOfDevices.numberOfDevices;
  delete dataWithoutNumberOfDevices.devicesWithAppliance;

  const tableRows = parseDataObjectToArray(dataWithoutNumberOfDevices);

  const props = useSpring({
    to: { opacity: 1 },
    from: { opacity: 0 },
  });
  const AnimatedTableContainer = animated(TableContainer);

  return (
    <AnimatedTableContainer component={Paper} style={props}>
      <Table aria-label="table">
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{ width: '200px' }}>
              Appliance
            </TableCell>
            <TableCell align="center">
              % Devices{' '}
              <InfoIcon
                fill="#388e3c"
                size={16}
                style={{
                  marginLeft: '10px',
                }}
                data-tip
                data-for="titleInfo"
              />
            </TableCell>
            <TableCell align="center" style={{ width: '170px' }}>
              # Devices
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow key={row.title}>
              <TableCell align="left">{row.title}</TableCell>
              <TableCell align="center">
                <LinearProgress
                  variant="determinate"
                  value={row.percentage}
                  style={{ height: '13px', borderRadius: '3px' }}
                />
              </TableCell>
              <TableCell align="center">
                {row.numberOfDevices}
                {rowHasTooltip(row) ? <InfoIcon fill="#388e3c" style={{marginLeft: '10px'}} data-tip data-for={row.key}/> : null}
              </TableCell>
            </TableRow>
          ))}
          <TableRow key="numberOfDevicesWithAppliances">
            <TableCell align="left">Devices With Appliances</TableCell>
            <TableCell align="center"></TableCell>
            <TableCell align="center">
              {data && parseNumberToString(data.devicesWithAppliance)}
              <InfoIcon fill="#388e3c" style={{marginLeft: '10px'}} data-tip data-for="devicesWithAppliancesInfo"/>
            </TableCell>
          </TableRow>
          <TableRow key="numberOfDevices">
            <TableCell align="left">Number of Devices</TableCell>
            <TableCell align="center"></TableCell>
            <TableCell align="center">
              {data && parseNumberToString(data.numberOfDevices)}
              <InfoIcon fill="#388e3c" style={{marginLeft: '10px'}} data-tip data-for="totalDevices"/>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <ReactTooltip id="titleInfo" place="top" type="dark" effect="solid">
        <div>
          This % represents how many users have that specific appliance in
          comparison to the total number of users that have filled the property
          information.
        </div>
        <div style={{ textAlign: 'center' }}>
          (users with that appliance) / (total number of users that filled the
          property information)
        </div>
      </ReactTooltip>
      <ReactTooltip id="devicesWithAppliancesInfo" place="top" type="dark" effect="solid">
        <div>Devices that the user does have informed appliances.</div>
      </ReactTooltip>
      <ReactTooltip id="totalDevices" place="top" type="dark" effect="solid">
        <div>This number indicates the quantity of devices that were online once in the last 30 days.</div>
      </ReactTooltip>
      <ReactTooltip id="withoutAppliancesPercentage" place="top" type="dark" effect="solid">
        <div>Devices that the user does not have informed appliances, in this counter is also included devices that does not have property information.</div>
      </ReactTooltip>
      <ReactTooltip id="withoutPropertyDetailsPercentage" place="top" type="dark" effect="solid">
        <div>This number indicates the quantity of devices that does not have property information of their “house”.</div>
      </ReactTooltip>
    </AnimatedTableContainer>
  );
}

ReportedTable.propTypes = {
  data: PropTypes.object.isRequired,
};

export default ReportedTable;
